import { useNavigate, useRouterState } from '@tanstack/react-router';
import { useCallback } from 'react';

/**
 * Wrapper around TanStack Router's useNavigate that preserves
 * current query parameters across navigations.
 *
 * Pass `search: { ... }` to merge extra params on top.
 * Pass `resetSearch: true` to drop all existing params.
 */
export function useNavigateWithParams() {
  const navigate = useNavigate();
  const currentSearch = useRouterState({
    select: (s) => s.location.search as Record<string, unknown>,
  });

  return useCallback(
    (opts: { to: string; search?: Record<string, unknown>; resetSearch?: boolean }) => {
      const { to, search, resetSearch, ...rest } = opts;

      const preserved = resetSearch ? {} : currentSearch;
      const merged = { ...preserved, ...search };

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      return navigate({ to, search: merged, ...rest } as any);
    },
    [navigate, currentSearch],
  );
}

/**
 * Append current query string to a plain href path.
 * Use for <a href={...}> links.
 */
export function useHrefWithParams() {
  const searchStr = useRouterState({
    select: (s) => s.location.searchStr,
  });

  return useCallback(
    (path: string): string => {
      if (!searchStr) return path;
      return path.includes('?')
        ? `${path}&${searchStr.slice(1)}`
        : `${path}${searchStr}`;
    },
    [searchStr],
  );
}
